import React, { useState, useEffect } from "react";
import { X, Calendar, Clock, User, Stethoscope, ChevronDown, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../../lib/utils";

// Demo data for UI design
const demoClients = [
  { id: 1, name: "Sarah Miller", email: "sarah@example.com", phone: "+44 7700 900123" },
  { id: 2, name: "Emily Johnson", email: "emily@example.com", phone: "+44 7700 900456" },
  { id: 3, name: "Rachel Adams", email: "rachel@example.com", phone: "+44 7700 900789" },
  { id: 4, name: "Jessica Lewis", email: "jessica@example.com", phone: "+44 7700 900321" },
  { id: 5, name: "Laura Brown", email: "laura@example.com", phone: "+44 7700 900654" },
];

const demoPractitioners = [
  { id: 1, name: "Dr. Saleema", specialty: "Injectables" },
  { id: 2, name: "Emma Taylor", specialty: "Skincare" },
  { id: 3, name: "Dr. Harper", specialty: "Laser Treatments" },
  { id: 4, name: "Kate Morgan", specialty: "Body Contouring" },
];

const demoTreatments = [
  { id: 1, name: "Botox - Upper Face", price: 280, duration: "30 min" },
  { id: 2, name: "Dermal Fillers - Lips", price: 350, duration: "45 min" },
  { id: 3, name: "Hydrafacial Treatment", price: 150, duration: "60 min" },
  { id: 4, name: "Chemical Peel", price: 120, duration: "45 min" },
  { id: 5, name: "Microneedling", price: 200, duration: "60 min" },
  { id: 6, name: "PRP Facial", price: 400, duration: "90 min" },
  { id: 7, name: "Laser Hair Removal", price: 180, duration: "30 min" },
  { id: 8, name: "LED Light Therapy", price: 80, duration: "30 min" },
];

interface BookingData {
  id?: number;
  clientId?: number;
  clientName?: string;
  practitionerId?: number;
  practitionerName?: string;
  treatmentId?: number;
  treatmentName?: string;
  date?: string;
  time?: string;
  status?: string;
}

interface AestheticBookingSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  booking?: any | null;
  mode?: "create" | "edit" | "view";
  onSave?: (data: BookingData) => void;
}

export default function AestheticBookingSidebar({
  isOpen,
  onClose,
  booking,
  mode = "create",
  onSave,
}: AestheticBookingSidebarProps) {
  const [selectedClient, setSelectedClient] = useState<typeof demoClients[0] | null>(null);
  const [selectedPractitioner, setSelectedPractitioner] = useState<typeof demoPractitioners[0] | null>(null);
  const [selectedTreatment, setSelectedTreatment] = useState<typeof demoTreatments[0] | null>(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [notes, setNotes] = useState("");

  // Dropdown states
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showPractitionerDropdown, setShowPractitionerDropdown] = useState(false);
  const [showTreatmentDropdown, setShowTreatmentDropdown] = useState(false);

  // Reset form when opening
  useEffect(() => {
    if (isOpen) {
      if (booking && mode === "edit") {
        // Populate form with existing booking data
        const client = demoClients.find(c => c.id === booking.clientId || c.name === booking.clientName);
        const practitioner = demoPractitioners.find(p => p.id === booking.practitionerId || p.name === booking.practitionerName);
        const treatment = demoTreatments.find(t => t.id === booking.treatmentId || t.name === booking.treatmentName);
        
        setSelectedClient(client || null);
        setSelectedPractitioner(practitioner || null);
        setSelectedTreatment(treatment || null);
        setSelectedDate(booking.date || "");
        setSelectedTime(booking.time || "");
      } else {
        // Reset for create mode
        setSelectedClient(null);
        setSelectedPractitioner(null);
        setSelectedTreatment(null);
        setSelectedDate(new Date().toISOString().split("T")[0]);
        setSelectedTime("10:00");
        setNotes("");
      }
    }
  }, [isOpen, booking, mode]);

  const handleSave = () => {
    const data: BookingData = {
      id: booking?.id || Date.now(),
      clientId: selectedClient?.id,
      clientName: selectedClient?.name,
      practitionerId: selectedPractitioner?.id,
      practitionerName: selectedPractitioner?.name,
      treatmentId: selectedTreatment?.id,
      treatmentName: selectedTreatment?.name,
      date: selectedDate,
      time: selectedTime,
      status: "Booked",
    };
    onSave?.(data);
    onClose();
  };

  const isFormValid = selectedClient && selectedPractitioner && selectedTreatment && selectedDate && selectedTime;

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  };

  const formatTime = (timeStr: string) => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  return (
    <>
      {/* Backdrop */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-[2px] z-[99]"
            onClick={onClose}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="fixed top-0 right-0 h-full w-full max-w-[500px] bg-[#F9F9F9] shadow-[-10px_0_30px_rgba(0,0,0,0.05)] border-l border-gray-100 z-[100] flex flex-col"
          >
            {/* Close Button */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
              className="absolute lg:-left-14 top-4 lg:top-8 right-4 lg:right-auto w-10 h-10 flex items-center justify-center rounded-full bg-white text-[#885ABB] shadow-lg lg:shadow-[-4px_0_12px_rgba(0,0,0,0.1)] hover:bg-[#402B69] hover:text-white transition-all z-[110] group"
            >
              <X size={20} strokeWidth={3} className="group-hover:rotate-90 transition-transform duration-300" />
            </motion.button>

            {/* Header */}
            <div className="bg-white border-b border-gray-100 shadow-sm z-10 sticky top-0">
              <div className="px-6 pt-10 pb-6">
                <h2 className="text-2xl font-bold text-[#402B69]">
                  {mode === "create" ? "New Booking" : mode === "edit" ? "Edit Booking" : "Booking Details"}
                </h2>
                <p className="text-sm text-gray-400 mt-1">
                  {mode === "create" ? "Create a new appointment" : mode === "edit" ? "Update appointment details" : "View appointment information"}
                </p>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              {/* Client Selection */}
              <div className="relative">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">
                  Client
                </label>
                <button
                  onClick={() => setShowClientDropdown(!showClientDropdown)}
                  className="w-full flex items-center justify-between px-4 py-3.5 bg-white border border-gray-200 rounded-xl text-left hover:border-[#885ABB] transition-all shadow-sm"
                >
                  {selectedClient ? (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#885ABB] to-[#C96EBE] text-white flex items-center justify-center font-semibold text-sm">
                        {selectedClient.name.charAt(0)}
                      </div>
                      <div>
                        <p className="font-semibold text-[#402B69]">{selectedClient.name}</p>
                        <p className="text-xs text-gray-500">{selectedClient.email}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 text-gray-400">
                      <User className="w-5 h-5" />
                      <span>Select a client...</span>
                    </div>
                  )}
                  <ChevronDown className={cn("w-5 h-5 text-gray-400 transition-transform", showClientDropdown && "rotate-180")} />
                </button>

                {showClientDropdown && (
                  <div className="absolute left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-xl z-50 max-h-60 overflow-y-auto">
                    {demoClients.map((client) => (
                      <button
                        key={client.id}
                        onClick={() => {
                          setSelectedClient(client);
                          setShowClientDropdown(false);
                        }}
                        className={cn(
                          "w-full text-left px-4 py-3 hover:bg-purple-50 transition flex items-center gap-3 border-b border-gray-50 last:border-0",
                          selectedClient?.id === client.id && "bg-purple-50"
                        )}
                      >
                        <div className="w-9 h-9 rounded-full bg-purple-100 text-[#885ABB] flex items-center justify-center font-semibold text-sm">
                          {client.name.charAt(0)}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-[#402B69]">{client.name}</p>
                          <p className="text-xs text-gray-500">{client.phone}</p>
                        </div>
                        {selectedClient?.id === client.id && (
                          <Check className="w-5 h-5 text-[#885ABB]" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Treatment Selection */}
              <div className="relative">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">
                  Treatment
                </label>
                <button
                  onClick={() => setShowTreatmentDropdown(!showTreatmentDropdown)}
                  className="w-full flex items-center justify-between px-4 py-3.5 bg-white border border-gray-200 rounded-xl text-left hover:border-[#885ABB] transition-all shadow-sm"
                >
                  {selectedTreatment ? (
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-10 h-10 rounded-xl bg-[#F8F5FC] text-[#885ABB] flex items-center justify-center">
                        <Stethoscope className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-[#402B69]">{selectedTreatment.name}</p>
                        <p className="text-xs text-gray-500">£{selectedTreatment.price} • {selectedTreatment.duration}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 text-gray-400">
                      <Stethoscope className="w-5 h-5" />
                      <span>Select a treatment...</span>
                    </div>
                  )}
                  <ChevronDown className={cn("w-5 h-5 text-gray-400 transition-transform", showTreatmentDropdown && "rotate-180")} />
                </button>

                {showTreatmentDropdown && (
                  <div className="absolute left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-xl z-50 max-h-72 overflow-y-auto">
                    {demoTreatments.map((treatment) => (
                      <button
                        key={treatment.id}
                        onClick={() => {
                          setSelectedTreatment(treatment);
                          setShowTreatmentDropdown(false);
                        }}
                        className={cn(
                          "w-full text-left px-4 py-3 hover:bg-purple-50 transition flex items-center justify-between border-b border-gray-50 last:border-0",
                          selectedTreatment?.id === treatment.id && "bg-purple-50"
                        )}
                      >
                        <div>
                          <p className="font-medium text-[#402B69]">{treatment.name}</p>
                          <p className="text-xs text-gray-500">{treatment.duration}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-[#885ABB]">£{treatment.price}</p>
                          {selectedTreatment?.id === treatment.id && (
                            <Check className="w-4 h-4 text-[#885ABB] inline ml-2" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Practitioner Selection */}
              <div className="relative">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">
                  Practitioner
                </label>
                <button
                  onClick={() => setShowPractitionerDropdown(!showPractitionerDropdown)}
                  className="w-full flex items-center justify-between px-4 py-3.5 bg-white border border-gray-200 rounded-xl text-left hover:border-[#885ABB] transition-all shadow-sm"
                >
                  {selectedPractitioner ? (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#5AB7BB] to-[#64B5F6] text-white flex items-center justify-center font-semibold text-sm">
                        {selectedPractitioner.name.charAt(0)}
                      </div>
                      <div>
                        <p className="font-semibold text-[#402B69]">{selectedPractitioner.name}</p>
                        <p className="text-xs text-gray-500">{selectedPractitioner.specialty}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 text-gray-400">
                      <User className="w-5 h-5" />
                      <span>Select a practitioner...</span>
                    </div>
                  )}
                  <ChevronDown className={cn("w-5 h-5 text-gray-400 transition-transform", showPractitionerDropdown && "rotate-180")} />
                </button>

                {showPractitionerDropdown && (
                  <div className="absolute left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-xl z-50 max-h-60 overflow-y-auto">
                    {demoPractitioners.map((practitioner) => (
                      <button
                        key={practitioner.id}
                        onClick={() => {
                          setSelectedPractitioner(practitioner);
                          setShowPractitionerDropdown(false);
                        }}
                        className={cn(
                          "w-full text-left px-4 py-3 hover:bg-purple-50 transition flex items-center gap-3 border-b border-gray-50 last:border-0",
                          selectedPractitioner?.id === practitioner.id && "bg-purple-50"
                        )}
                      >
                        <div className="w-9 h-9 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center font-semibold text-sm">
                          {practitioner.name.charAt(0)}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-[#402B69]">{practitioner.name}</p>
                          <p className="text-xs text-gray-500">{practitioner.specialty}</p>
                        </div>
                        {selectedPractitioner?.id === practitioner.id && (
                          <Check className="w-5 h-5 text-[#885ABB]" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Date & Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">
                    Date
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className="w-full px-4 py-3.5 bg-white border border-gray-200 rounded-xl text-[#402B69] font-medium hover:border-[#885ABB] focus:border-[#885ABB] focus:outline-none transition-all shadow-sm appearance-none"
                    />
                    <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#885ABB] pointer-events-none" />
                  </div>
                  {selectedDate && (
                    <p className="text-xs text-gray-500 mt-1.5 pl-1">{formatDate(selectedDate)}</p>
                  )}
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">
                    Time
                  </label>
                  <div className="relative">
                    <input
                      type="time"
                      value={selectedTime}
                      onChange={(e) => setSelectedTime(e.target.value)}
                      className="w-full px-4 py-3.5 bg-white border border-gray-200 rounded-xl text-[#402B69] font-medium hover:border-[#885ABB] focus:border-[#885ABB] focus:outline-none transition-all shadow-sm appearance-none"
                    />
                    <Clock className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#885ABB] pointer-events-none" />
                  </div>
                  {selectedTime && (
                    <p className="text-xs text-gray-500 mt-1.5 pl-1">{formatTime(selectedTime)}</p>
                  )}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">
                  Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any special notes or instructions..."
                  rows={3}
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#402B69] placeholder-gray-400 hover:border-[#885ABB] focus:border-[#885ABB] focus:outline-none transition-all shadow-sm resize-none"
                />
              </div>

              {/* Booking Summary Card */}
              {selectedTreatment && (
                <div className="bg-gradient-to-br from-[#F8F5FC] to-white border border-purple-100 rounded-2xl p-5 mt-4">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">Booking Summary</h4>
                  <div className="space-y-3">
                    {selectedClient && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Client</span>
                        <span className="font-medium text-[#402B69]">{selectedClient.name}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Treatment</span>
                      <span className="font-medium text-[#402B69]">{selectedTreatment.name}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Duration</span>
                      <span className="font-medium text-[#402B69]">{selectedTreatment.duration}</span>
                    </div>
                    {selectedPractitioner && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Practitioner</span>
                        <span className="font-medium text-[#402B69]">{selectedPractitioner.name}</span>
                      </div>
                    )}
                    {selectedDate && selectedTime && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Date & Time</span>
                        <span className="font-medium text-[#402B69]">{formatDate(selectedDate)} at {formatTime(selectedTime)}</span>
                      </div>
                    )}
                    <div className="border-t border-purple-100 pt-3 mt-3">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-[#402B69]">Total</span>
                        <span className="text-xl font-bold text-[#885ABB]">£{selectedTreatment.price}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div className="bg-white border-t border-gray-100 p-6 space-y-3">
              <button
                onClick={handleSave}
                disabled={!isFormValid}
                className={cn(
                  "w-full py-4 rounded-xl font-semibold text-sm uppercase tracking-wide transition-all shadow-lg",
                  isFormValid
                    ? "bg-[#885ABB] text-white hover:bg-[#7248A8] hover:shadow-xl"
                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                )}
              >
                {mode === "create" ? "Create Booking" : "Save Changes"}
              </button>
              <button
                onClick={onClose}
                className="w-full py-3 rounded-xl font-semibold text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-all"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

